import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Grid, File } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable from '../shared/ReportTable';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]
    .map((y) => ({ value: String(y), label: String(y) }));

const money = (v) => Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Colored ±% cell. 0 / -100 / '-' render as a plain dash.
const renderPct = (value) => {
    if (value === '-' || value == null) return '-';
    const n = parseFloat(value);
    if (Number.isNaN(n) || n === 0 || n === -100) return '-';
    const color = n > 0 ? 'var(--v3-green)' : 'var(--v3-red)';
    const arrow = n > 0 ? '↑' : '↓';
    return <span style={{ color }}>{value}% {arrow}</span>;
};
const pctStr = (value) => {
    if (value === '-' || value == null) return '-';
    const n = parseFloat(value);
    if (Number.isNaN(n) || n === 0 || n === -100) return '-';
    return `${value}`;
};

const initialFilters = {
    year: String(CURRENT_YEAR),
    property_id: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    tenant_id: '',
};

const GrowthAnalysisView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const [monthly, setMonthly] = useState({ currentYearSales: [], previousYearSales: [] });
    const [yearly, setYearly] = useState({});
    const [currentYearTotal, setCurrentYearTotal] = useState(0);
    const [previousYearTotal, setPreviousYearTotal] = useState(0);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.tenant_id = ''; }
            if (key === 'property_id') { next.tenant_id = ''; }
            if (key === 'subcategory_id') { next.tenant_id = ''; }
            if (key === 'group_name') { next.tenant_id = ''; }
            return next;
        });
        setMonthly({ currentYearSales: [], previousYearSales: [] });
        setYearly({});
        setHasRun(false);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setMonthly({ currentYearSales: [], previousYearSales: [] });
        setYearly({});
        setHasRun(false);
        setError('');
    };

    const generateReport = async () => {
        setLoading(true);
        setError('');
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            year: filters.year,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_name: nameOf(lookups.tenantOptions, filters.tenant_id),
        };
        try {
            const [mRes, yRes] = await Promise.all([
                axios.post(`${config.hostname}/reports/getmonthlygrowthanalysisreport`, payload, { headers }),
                axios.post(`${config.hostname}/reports/getyearlygrowthanalysisreport`, payload, { headers }),
            ]);
            setMonthly(mRes.data || { currentYearSales: [], previousYearSales: [] });
            setCurrentYearTotal(mRes.data?.totalCurrentYearSales || 0);
            setPreviousYearTotal(mRes.data?.totalPreviousYearSales || 0);
            setYearly(yRes.data || {});
            setHasRun(true);
            insertToLog(' generated Growth Analysis report');
        } catch (err) {
            console.error('growth analysis report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Growth Analysis report'));
        } finally {
            setLoading(false);
        }
    };

    const yr = Number(filters.year);

    // ── Yearly table ────────────────────────────────────────────────
    const yearlyColumns = [
        { key: 'year', label: t('Year'), sortable: false },
        { key: 'sales', label: t('Sales'), align: 'right', sortable: false },
        { key: 'yoy', label: t('YOY'), align: 'right', sortable: false, render: (r) => (r.yoy === '-' ? '-' : renderPct(r.yoy)) },
        { key: 'ytd', label: t('YTD'), align: 'right', sortable: false },
        { key: 'ytd_change', label: t('YTD Change'), align: 'right', sortable: false, render: (r) => (r.ytd_change === '-' ? '-' : renderPct(r.ytd_change)) },
        { key: 'mtd', label: t('MTD'), align: 'right', sortable: false },
        { key: 'mtd_change', label: t('MTD Change'), align: 'right', sortable: false, render: (r) => (r.mtd_change === '-' ? '-' : renderPct(r.mtd_change)) },
    ];
    const yearlyRows = useMemo(() => {
        if (!yearly || !Object.keys(yearly).length) return [];
        return [
            {
                year: yr - 1,
                sales: money(yearly.previousYearSale),
                yoy: '-',
                ytd: money(yearly.ytdPreviousYear),
                ytd_change: '-',
                mtd: money(yearly.mtdPreviousYear),
                mtd_change: '-',
            },
            {
                year: yr,
                sales: money(yearly.currentYearSale),
                yoy: yearly.yoyCurrentYear,
                ytd: money(yearly.ytdCurrentYear),
                ytd_change: yearly.growthYTD,
                mtd: money(yearly.mtdCurrentYear),
                mtd_change: yearly.growthMTD,
            },
        ];
    }, [yearly, yr]);

    // ── Monthly table ───────────────────────────────────────────────
    const monthlyColumns = [
        { key: 'month', label: t('Month'), sortable: false },
        { key: 'prevSales', label: `${yr - 1}`, align: 'right', sortable: false },
        { key: 'prevMom', label: t('MOM Change'), align: 'right', sortable: false, render: (r) => renderPct(r.prevMom) },
        { key: 'curSales', label: `${yr}`, align: 'right', sortable: false },
        { key: 'curMom', label: t('MOM Change'), align: 'right', sortable: false, render: (r) => renderPct(r.curMom) },
        { key: 'yoyChange', label: t('YOY Change'), align: 'right', sortable: false, render: (r) => renderPct(r.yoyChange) },
    ];
    const monthlyRows = useMemo(() => (monthly.previousYearSales || []).map((item, i) => {
        const cur = monthly.currentYearSales[i] || {};
        return {
            month: item.month,
            prevSales: money(item.totalSales),
            prevMom: item.percentageChange,
            curSales: money(cur.totalSales),
            curMom: cur.percentageChange,
            yoyChange: cur.YOYChange,
        };
    }), [monthly]);

    const monthlyTotals = useMemo(
        () => (monthlyRows.length ? { prevSales: Number(previousYearTotal).toLocaleString(), curSales: Number(currentYearTotal).toLocaleString() } : null),
        [monthlyRows, previousYearTotal, currentYearTotal],
    );

    const fields = [
        { key: 'year', label: t('Year'), type: 'select', options: YEAR_OPTIONS, placeholder: t('Select year') },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'tenant_id', label: t('Tenant'), type: 'select', options: lookups.tenantOptions, placeholder: t('All tenants') },
    ];

    const meta = () => ([
        [t('Year'), filters.year],
        [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
        [t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')],
        [t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')],
        [t('Group'), filters.group_name || t('All')],
        [t('Tenant'), nameOf(lookups.tenantOptions, filters.tenant_id) || t('All')],
        [t('Exported By'), login_name],
        [t('Exported On'), new Date().toLocaleString()],
    ]);

    const exportToExcel = () => {
        if (!hasRun) return;
        const wb = XLSX.utils.book_new();
        const yHead = [t('Year'), t('Sales'), t('YOY'), t('YTD'), t('Change'), t('MTD'), t('Change')];
        const yData = yearlyRows.map((r) => [r.year, r.sales, pctStr(r.yoy), r.ytd, pctStr(r.ytd_change), r.mtd, pctStr(r.mtd_change)]);
        const mHead = [t('Month'), `${yr - 1}`, t('MOM Change'), `${yr}`, t('MOM Change'), t('YOY Change')];
        const mData = monthlyRows.map((r) => [r.month, r.prevSales, pctStr(r.prevMom), r.curSales, pctStr(r.curMom), pctStr(r.yoyChange)]);
        const combined = [
            [t('Growth Analysis Report')], ...meta(), [],
            [t('Yearly Analysis')], yHead, ...yData, [],
            [t('Monthly Analysis')], mHead, ...mData,
        ];
        const ws = XLSX.utils.aoa_to_sheet(combined);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
        XLSX.utils.book_append_sheet(wb, ws, 'Growth Analysis Report');
        XLSX.writeFile(wb, 'GrowthAnalysisReport.xlsx');
        insertToLog(' exported Growth Analysis report to excel');
    };

    const exportToPDF = () => {
        if (!hasRun) return;
        const doc = new jsPDF();
        doc.setFontSize(12);
        doc.text(t('Growth Analysis Report'), 14, 14);
        doc.setFontSize(8);
        let y = 22;
        meta().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 14, y); y += 5; });
        doc.setFontSize(13);
        doc.text(t('Yearly Analysis'), 14, y + 4);
        doc.autoTable({
            head: [[t('Year'), t('Sales'), t('YOY'), t('YTD'), t('Change'), t('MTD'), t('Change')]],
            body: yearlyRows.map((r) => [r.year, r.sales, pctStr(r.yoy), r.ytd, pctStr(r.ytd_change), r.mtd, pctStr(r.mtd_change)]),
            startY: y + 8,
            styles: { fontSize: 8 },
        });
        const fY = doc.lastAutoTable.finalY + 8;
        doc.setFontSize(13);
        doc.text(t('Monthly Analysis'), 14, fY);
        doc.autoTable({
            head: [[t('Month'), `${yr - 1}`, t('MOM Change'), `${yr}`, t('MOM Change'), t('YOY Change')]],
            body: monthlyRows.map((r) => [r.month, r.prevSales, pctStr(r.prevMom), r.curSales, pctStr(r.curMom), pctStr(r.yoyChange)]),
            startY: fY + 4,
            styles: { fontSize: 8 },
        });
        doc.save('GrowthAnalysisReport.pdf');
        insertToLog(' exported Growth Analysis report to pdf');
    };

    if (!allowed) return null;

    const emptyY = hasRun ? t('No yearly data for the selected filters.') : t('Choose your filters and select Generate report to view results.');
    const emptyM = hasRun ? t('No monthly data for the selected filters.') : t('Choose your filters and select Generate report to view results.');

    return (
        <Fragment>
            <ReportShell
                title={t('Growth Analysis Report')}
                subtitle={t('Year-over-year and month-over-month sales growth.')}
                actions={(
                    <Fragment>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToExcel} disabled={!hasRun} title={t('Export to Excel')}>
                            <Grid size={14} /> {t('Excel')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToPDF} disabled={!hasRun} title={t('Export to PDF')}>
                            <File size={14} /> {t('PDF')}
                        </button>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <h3 className="v3-heading" style={{ margin: '4px 0 0' }}>{t('Yearly Analysis')}</h3>
                <ReportTable
                    columns={yearlyColumns}
                    rows={yearlyRows}
                    loading={loading}
                    error={error}
                    emptyMessage={emptyY}
                />

                <h3 className="v3-heading" style={{ margin: '4px 0 0' }}>{t('Monthly Analysis')}</h3>
                <ReportTable
                    columns={monthlyColumns}
                    rows={monthlyRows}
                    totals={monthlyTotals}
                    loading={loading}
                    error={error}
                    emptyMessage={emptyM}
                />
            </ReportShell>
        </Fragment>
    );
};

export default GrowthAnalysisView;
